// ============================================================
// SpringBootMapStruct.jsx
//
// A branded, responsive course content page for the
// "MapStruct for Type-Safe Mapping" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Annotation processor setup, Mapper interface
//           patterns, and compile-time generation benefits.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import FlashOnIcon            from "@mui/icons-material/FlashOn";
import AltRouteIcon           from "@mui/icons-material/AltRoute";
import MemoryIcon             from "@mui/icons-material/Memory";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootMapStruct() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 13"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Type-Safe <span>MapStruct Mapping</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Eliminate manual object mapping boilerplate. MapStruct is an annotation processor that generates lightning-fast, compile-time mapping code, ensuring type safety and high performance without the overhead of runtime reflection.
                    </Typography>
                </Box>

                {/* 1. BUILD SETUP */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Build Configuration" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        MapStruct requires an annotation processor to generate the implementation classes during the compile phase.
                    </Typography>
                    <CodeBlock
                        title="Maven dependency & Processor"
                        code={`<dependency>\n  <groupId>org.mapstruct</groupId>\n  <artifactId>mapstruct</artifactId>\n  <version>1.5.5.Final</version>\n</dependency>\n\n<annotationProcessorPaths>\n  <path>\n    <groupId>org.mapstruct</groupId>\n    <artifactId>mapstruct-processor</artifactId>\n    <version>1.5.5.Final</version>\n  </path>\n</annotationProcessorPaths>`}
                    />
                </Box>

                {/* 2. THE MAPPER INTERFACE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AltRouteIcon />} label="2. Defining the Mapper" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Simply define an interface. MapStruct will automatically generate the implementation class with efficient getter/setter logic.
                    </Typography>
                    <CodeBlock
                        title="UserMapper.java"
                        code={`@Mapper(componentModel = "spring")\npublic interface UserMapper {\n    // Standard fields are mapped automatically\n    UserDto toDto(User user);\n\n    @Mapping(target = "password", ignore = true)\n    User toEntity(UserDto dto);\n}`}
                    />
                </Box>

                {/* 3. USAGE IN SPRING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<IntegrationInstructionsIcon />} label="3. Dependency Injection" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        By setting <code style={{color: BRAND.white}}>componentModel = "spring"</code>, the generated mapper becomes a Spring Bean that you can inject anywhere.
                    </Typography>
                    <CodeBlock
                        title="UserController.java"
                        code={`@RestController\npublic class UserController {\n    private final UserMapper mapper;\n\n    public UserController(UserMapper mapper) {\n        this.mapper = mapper;\n    }\n\n    @GetMapping("/{id}")\n    public UserDto get(@PathVariable Long id) {\n        return mapper.toDto(service.find(id));\n    }\n}`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ADVANTAGES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Why Choose MapStruct?" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Blazing Performance", desc: "No reflection used. Generated code is as fast as manual mapping.", icon: <FlashOnIcon /> },
                            { title: "Compile-Time Safety", desc: "Errors are caught during the build, not at runtime when data is missing.", icon: <MemoryIcon /> },
                            { title: "Fluent Customization", desc: "Easily handle field name mismatches using the @Mapping annotation.", icon: <CodeIcon /> }
                        ].map((item, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> MapStruct works beautifully with <strong>Lombok</strong>. Ensure the MapStruct processor is listed <i>after</i> the Lombok processor in your build configuration to ensure the getters/setters exist before mapping begins.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
